/**
 * Proyecto: MOGOTAX-SERVER
 * Autor: Megatrack GPS SAS
 * Fecha de creación: 8 de noviembre 2025
 * Licencia: Propiedad Privada
 *
 * Este código es propiedad exclusiva de Megatrack GPS SAS & Mogotax SAS y no puede ser distribuido, copiado, modificado, publicado, sublicenciado ni comercializado sin el consentimiento expreso y por escrito de ambas empresa.
 *
 * Copyright (c) 2025 Megatrack GPS SAS. Todos los derechos reservados.
 */
import React, { useState } from 'react';
import { Modal, Button, Checkbox, Typography, Divider, Space, message } from 'antd';
import { CheckCircleOutlined, SafetyOutlined } from '@ant-design/icons';
import { useTerms } from '../Contexts/TermsContext';

const { Title, Paragraph, Text } = Typography;

const PrivacyPolicyModal = () => {
  const { mostrarModalPolitica, aceptarPoliticaPrivacidad } = useTerms();
  const [aceptado, setAceptado] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleAceptar = async () => {
    if (!aceptado) {
      message.warning('Debes aceptar la política de privacidad para continuar');
      return;
    }

    setLoading(true);
    const success = await aceptarPoliticaPrivacidad();
    setLoading(false);

    if (success) {
      message.success('Política de privacidad aceptada correctamente');
    } else {
      message.error('Error al aceptar la política. Por favor intenta nuevamente');
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SafetyOutlined style={{ fontSize: 24, color: '#52c41a' }} />
          <span>Política de Privacidad y Tratamiento de Datos</span>
        </div>
      }
      open={mostrarModalPolitica}
      closable={false}
      maskClosable={false}
      width={window.innerWidth > 768 ? 700 : '95%'}
      footer={[
        <div key="footer" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Checkbox
            checked={aceptado}
            onChange={(e) => setAceptado(e.target.checked)}
            style={{ fontSize: '14px' }}
          >
            He leído y acepto la política de privacidad y tratamiento de datos personales de MOGOTAX
          </Checkbox>
          <Button
            type="primary"
            size="large"
            icon={<CheckCircleOutlined />}
            onClick={handleAceptar}
            disabled={!aceptado}
            loading={loading}
            block
          >
            Aceptar y Continuar
          </Button>
        </div>
      ]}
    >
      <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '0 8px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Text type="secondary">Fecha de última actualización: Noviembre 2025</Text>
          </div>

          <Divider />

          <div>
            <Title level={5}>1. RESPONSABLE DEL TRATAMIENTO</Title>
            <Paragraph>
              MOGOTAX SAS, identificada con NIT XXX.XXX.XXX-X, con domicilio en [Dirección],
              es el responsable del tratamiento de sus datos personales.
            </Paragraph>
          </div>

          <div>
            <Title level={5}>2. DATOS PERSONALES QUE RECOPILAMOS</Title>
            <Paragraph>
              Recopilamos y tratamos las siguientes categorías de datos personales:
            </Paragraph>
            <ul>
              <li><strong>Datos de identificación:</strong> Nombre completo, tipo y número de documento,
                fecha de nacimiento, fotografía</li>
              <li><strong>Datos de contacto:</strong> Dirección, teléfono, correo electrónico</li>
              <li><strong>Datos laborales:</strong> Licencia de conducción, categoría, fecha de vencimiento,
                ARL, fecha de ingreso</li>
              <li><strong>Datos de salud:</strong> Grupo sanguíneo, contacto de emergencia</li>
              <li><strong>Datos de geolocalización:</strong> Ubicación en tiempo real durante los traslados</li>
              <li><strong>Firma digital:</strong> Imagen de su firma para validación de servicios</li>
              <li><strong>Datos de uso:</strong> Historial de traslados, tiempos de servicio, rutas utilizadas</li>
            </ul>
          </div>

          <div>
            <Title level={5}>3. FINALIDAD DEL TRATAMIENTO</Title>
            <Paragraph>
              Sus datos personales serán utilizados para:
            </Paragraph>
            <ul>
              <li>Gestionar su vinculación como conductor de MOGOTAX</li>
              <li>Asignar y coordinar traslados de pacientes</li>
              <li>Realizar seguimiento en tiempo real de los servicios</li>
              <li>Generar documentación legal de los traslados realizados</li>
              <li>Garantizar la seguridad de pacientes y conductores</li>
              <li>Realizar auditorías de calidad del servicio</li>
              <li>Cumplir con obligaciones legales y regulatorias</li>
              <li>Procesar pagos y liquidaciones</li>
              <li>Comunicarnos con usted sobre temas relacionados con el servicio</li>
              <li>Mejorar nuestros servicios y la experiencia del usuario</li>
            </ul>
          </div>

          <div>
            <Title level={5}>4. BASE LEGAL DEL TRATAMIENTO</Title>
            <Paragraph>
              El tratamiento de sus datos personales se fundamenta en:
            </Paragraph>
            <ul>
              <li>Su consentimiento explícito al aceptar esta política</li>
              <li>La ejecución del contrato de prestación de servicios</li>
              <li>El cumplimiento de obligaciones legales</li>
              <li>El interés legítimo en la prestación del servicio de transporte sanitario</li>
            </ul>
          </div>

          <div>
            <Title level={5}>5. COMPARTIR DATOS CON TERCEROS</Title>
            <Paragraph>
              Podemos compartir sus datos personales con:
            </Paragraph>
            <ul>
              <li><strong>Entidades de salud:</strong> Para coordinación de traslados y facturación</li>
              <li><strong>Autoridades competentes:</strong> Cuando sea requerido por ley</li>
              <li><strong>Proveedores de servicios:</strong> Que nos apoyan en la operación
                (servicios de nube, geolocalización, etc.)</li>
              <li><strong>Aseguradoras:</strong> En caso de incidentes que requieran activación de pólizas</li>
            </ul>
            <Paragraph>
              Todos los terceros con quienes compartimos información están obligados a proteger
              sus datos conforme a la normativa vigente.
            </Paragraph>
          </div>

          <div>
            <Title level={5}>6. TRANSFERENCIAS INTERNACIONALES</Title>
            <Paragraph>
              Algunos de nuestros proveedores de servicios pueden estar ubicados fuera de Colombia.
              En estos casos, garantizamos que se implementen las medidas de seguridad adecuadas
              para proteger sus datos conforme a la normativa colombiana.
            </Paragraph>
          </div>

          <div>
            <Title level={5}>7. TIEMPO DE CONSERVACIÓN</Title>
            <Paragraph>
              Conservaremos sus datos personales durante:
            </Paragraph>
            <ul>
              <li>El tiempo que dure su relación laboral o contractual con MOGOTAX</li>
              <li>Los períodos establecidos por la normativa legal para conservación de documentos
                (mínimo 10 años para documentos relacionados con servicios de salud)</li>
              <li>El tiempo necesario para resolver cualquier reclamación o litigio</li>
            </ul>
          </div>

          <div>
            <Title level={5}>8. MEDIDAS DE SEGURIDAD</Title>
            <Paragraph>
              Implementamos medidas técnicas y organizativas para proteger sus datos personales:
            </Paragraph>
            <ul>
              <li>Cifrado de datos en tránsito y en reposo</li>
              <li>Control de acceso basado en roles y autenticación segura</li>
              <li>Copias de seguridad periódicas</li>
              <li>Auditorías de seguridad regulares</li>
              <li>Capacitación del personal en protección de datos</li>
            </ul>
          </div>

          <div>
            <Title level={5}>9. SUS DERECHOS COMO TITULAR</Title>
            <Paragraph>
              De acuerdo con la Ley 1581 de 2012 y sus decretos reglamentarios, usted tiene
              derecho a:
            </Paragraph>
            <ul>
              <li><strong>Conocer:</strong> Qué datos tenemos sobre usted y cómo los usamos</li>
              <li><strong>Actualizar:</strong> Solicitar la corrección de datos inexactos</li>
              <li><strong>Rectificar:</strong> Modificar datos incompletos o incorrectos</li>
              <li><strong>Suprimir:</strong> Solicitar la eliminación de sus datos cuando sea procedente</li>
              <li><strong>Revocar:</strong> Retirar su consentimiento en cualquier momento</li>
              <li><strong>Consultar:</strong> Acceder a sus datos personales en nuestro poder</li>
            </ul>
            <Paragraph>
              Para ejercer estos derechos, puede contactarnos en:
            </Paragraph>
            <ul>
              <li>Email: protecciondatos@mogotaxsas.com</li>
              <li>Dirección: [Dirección física]</li>
              <li>Teléfono: +57 (601) XXX-XXXX</li>
            </ul>
          </div>

          <div>
            <Title level={5}>10. GEOLOCALIZACIÓN</Title>
            <Paragraph>
              La aplicación utiliza servicios de geolocalización para rastrear su ubicación
              en tiempo real durante los traslados. Esta información es esencial para:
            </Paragraph>
            <ul>
              <li>Garantizar la seguridad del paciente y del conductor</li>
              <li>Optimizar rutas y tiempos de servicio</li>
              <li>Generar evidencia en caso de incidentes</li>
              <li>Realizar auditorías de calidad</li>
            </ul>
            <Paragraph>
              Los datos de geolocalización solo se recopilan cuando la aplicación está activa
              y durante la prestación del servicio. Puede desactivar la geolocalización en
              cualquier momento, pero esto limitará su capacidad de utilizar la aplicación.
            </Paragraph>
          </div>

          <div>
            <Title level={5}>11. COOKIES Y TECNOLOGÍAS SIMILARES</Title>
            <Paragraph>
              La aplicación puede utilizar cookies y tecnologías similares para mejorar
              la experiencia del usuario y recopilar información sobre el uso del servicio.
              Puede configurar su dispositivo para rechazar cookies, aunque esto puede afectar
              algunas funcionalidades.
            </Paragraph>
          </div>

          <div>
            <Title level={5}>12. CAMBIOS EN LA POLÍTICA</Title>
            <Paragraph>
              Nos reservamos el derecho de actualizar esta política de privacidad en cualquier
              momento. Los cambios significativos serán notificados a través de la aplicación
              y requerirán su aceptación para continuar utilizando el servicio.
            </Paragraph>
          </div>

          <div>
            <Title level={5}>13. AUTORIDAD DE CONTROL</Title>
            <Paragraph>
              Si considera que sus derechos han sido vulnerados, puede presentar una queja
              ante la Superintendencia de Industria y Comercio (SIC), autoridad de protección
              de datos en Colombia.
            </Paragraph>
          </div>

          <Divider />

          <div style={{ textAlign: 'center', paddingBottom: 16 }}>
            <Text type="secondary" italic>
              Al hacer clic en "Aceptar y Continuar", autorizo expresamente a MOGOTAX SAS
              para recopilar, almacenar, usar, circular y suprimir mis datos personales
              conforme a lo establecido en esta política de privacidad.
            </Text>
          </div>
        </Space>
      </div>
    </Modal>
  );
};

export default PrivacyPolicyModal;
